import { AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import "./chatPanel.scss";
import CountriesModal from "./helpers/CountriesModal.jsx/CountriesModal";

import ChatMessage from "./helpers/ChatMessage";
import {
  applyOneLineWidthLimit,
  botVariant,
  userVariant,
} from "./helpers/chatFlow";
import { useChatFlow } from "./helpers/useChatFlow";

export default function ChatPanel({ sceneActive, playScene, storageKey }) {
  const {
    input,
    setInput,
    step,
    messages,
    editingId,
    editingText,
    setEditingText,
    verificationMethod,
    communicationPlatform,
    useSameAccountChoice,
    selectedNumbersMode,
    isRadioStep,
    canSend,
    startEdit,
    send,
    saveEdit,
    selectVerificationOption,
    selectPlatformOption,
    selectSameAccountOption,
    selectNumbersOption,
    uploadedDoc,
    uploading,
    uploadDocument,
    countriesModalOpen,
    closeCountriesModal,
    pickCountry,
  } = useChatFlow({ sceneActive, playScene, storageKey });

  const listRef = useRef(null);
  const bottomRef = useRef(null);
  const [stickToBottom, setStickToBottom] = useState(true);

  const onSendKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      send();
    }
  };

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const onScroll = () => {
      const threshold = 80;
      const atBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
      setStickToBottom(atBottom);
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!stickToBottom) return;
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, stickToBottom]);

  return (
    <div className="chat__sms">
      <div
        className="chat__sms__messages chat__sms__messages--hideScroll"
        ref={listRef}
      >
        <AnimatePresence>
          {messages.map((m) => (
            <ChatMessage
              key={m.id}
              m={m}
              botVariant={botVariant}
              userVariant={userVariant}
              sceneActive={sceneActive}
              editingId={editingId}
              editingText={editingText}
              setEditingText={setEditingText}
              verificationMethod={verificationMethod}
              communicationPlatform={communicationPlatform}
              useSameAccountChoice={useSameAccountChoice}
              selectedNumbersMode={selectedNumbersMode}
              step={step}
              startEdit={startEdit}
              saveEdit={saveEdit}
              selectVerificationOption={selectVerificationOption}
              selectPlatformOption={selectPlatformOption}
              selectSameAccountOption={selectSameAccountOption}
              selectNumbersOption={selectNumbersOption}
              uploadedDoc={uploadedDoc}
              uploading={uploading}
              uploadDocument={uploadDocument}
            />
          ))}
        </AnimatePresence>

        <div ref={bottomRef} style={{ height: 1 }} />
      </div>

      <div className="chat__sms__inputText">
        <textarea
          placeholder={
            isRadioStep
              ? "Select an option above"
              : step === 10
                ? "Upload a document above"
                : "Send a message"
          }
          rows={1}
          value={input}
          disabled={isRadioStep || step === 10}
          onKeyDown={onSendKeyDown}
          onChange={(e) => {
            const el = e.target;
            const prev = input;
            applyOneLineWidthLimit(e.target.value, el, prev, setInput);
          }}
        />
        <button
          onClick={send}
          disabled={!canSend}
          className={["chat__sms__inputText__sendBtn", !canSend && "isDisabled"]
            .filter(Boolean)
            .join(" ")}
        >
          Send
        </button>
      </div>

      <CountriesModal
        open={countriesModalOpen}
        onClose={closeCountriesModal}
        onPick={pickCountry}
      />
    </div>
  );
}
